
import React, { createContext, useEffect, useState, useContext } from 'react';
import { saveDataSet, getDataSetItems } from '../services/data';
import { reconcileMetadataWithSchema } from '../utils/datasetMetadata';
import { AppContext } from './AppContext';

const DataSetsContext = createContext();

const getDefaultIngestionWorkflow = () => {
  const defaultNodes = [
    {
      id: "1",
      position: { x: 50, y: (window.innerHeight / 2) - 150 },
      data: { label: "Input Document" },
      type: "inputDocumentNode",
    },
    {
      id: "2",
      position: { x: window.innerWidth - 1500, y: (window.innerHeight / 2) - 150 },
      data: { label: "Text Splitting" },
      type: "textSplittingNode",
    },
    {
      id: "3",
      position: { x: window.innerWidth - 1000, y: (window.innerHeight / 2) - 150 },
      data: { label: "Chroma DB" },
      type: "chromaInputNode",
    },
  ];

  const defaultEdges = [
    {
      id: "e1-2",
      source: "1",
      target: "2",
      label: "document-text",
    }, {
      id: "e2-3",
      source: "2",
      target: "3",
      label: "workflow-output",
    }
  ];

  return {
    nodes: defaultNodes,
    edges: defaultEdges,
  }
}

export const DataSetsProvider = ({ children }) => {
  const [dataSets, setDataSets] = useState([]);
  const [selectedDataSet, setSelectedDataSet] = useState(null);
  const [updatedDataSet, setUpdatedDataSet] = useState();
  const [updatedDataItem, setUpdatedDataItem] = useState();
  const [selectedDataSetItems, setSelectedDataSetItems] = useState([]);
  const [selectedDataItem, setSelectedDataItem] = useState(null);
  const [selectedDataItemChunks, setSelectedDataItemChunks] = useState([]);
  const [loadingDataSetItem, setLoadingDataSetItem] = useState(false);
  const [loadingDataSetItemChunks, setLoadingDataSetItemChunks] = useState(false);

  /*{
    public_id: "",
    name: "",
    description: "",
    ingestion_workflow: getDefaultIngestionWorkflow(),
  }
);*/

  const { selectedClient } = useContext(AppContext);
  console.log("!!! SELECTED CLIENT IN data SETS CONTEXT: ", selectedClient);

  useEffect(() => {
    console.log("!!! UPDATED data SET CHANGED: ", updatedDataSet);
    // Get the data set items
    const fetchDataSetItems = async () => {
      console.log("!!! FETCHING DATA SET ITEMS FOR data SET: ", updatedDataSet);
      if (updatedDataSet && updatedDataSet.public_id) {
        const results = await getDataSetItems(updatedDataSet.public_id);
        console.log("!!! DATA SET ITEMS RESULTS: ", results);
        if (results.success) {
          setSelectedDataSetItems(results.items || []);
        }
      } else {
        setSelectedDataSetItems([]);
      }
    }

    fetchDataSetItems();
  }, [updatedDataSet]);

  useEffect(() => {
    if (selectedDataItem) {
      console.log("!!! SELECTED data SET ITEM CHANGED: ", selectedDataItem);
    } else {
      setSelectedDataItemChunks();
    }
  }, [selectedDataItem]);

  const addDataSet = (dataSet) => {
    setDataSets([...dataSets, dataSet]);
  };

  const removeDataSet = (dataSetId) => {
    setDataSets(dataSets.filter(dataSet => dataSet.id !== dataSetId));
  };

  const getDataSetItem = (mediaId) => {
    const fetchDataItem = async () => {
      setLoadingDataSetItem(true);
      setLoadingDataSetItem(false);
    }
    fetchDataItem();
  }

  const getDataSetItemChunks = (mediaId) => {
    console.log("!!! GETTING data ITEM WITH ID: ", mediaId, updatedDataSet?.public_id);
    const fetchDataItemChunks = async () => {
      setLoadingDataSetItemChunks(true);
      setLoadingDataSetItemChunks(false);
    }
    fetchDataItemChunks();
  }

  const saveUpdatedDataSet = async () => {
    console.log("!!! SAVE UPDATED data SET CALLED: ", updatedDataSet);


    /*updatedDataSet.published = updatedDataSet.published ?? false;
    updatedDataSet.timezone = getFullTimeZone()
    updatedDataSet.open_date = updatedDataSet.local_open_date;
    updatedDataSet.start_date = updatedDataSet.local_start_date;
    updatedDataSet.recording_date = updatedDataSet.local_recording_date;*/

    //const newDataSet = !updatedDataSet.public_id;

    // Apply the dataset's metadata schema to its metadata before saving: fields the schema no
    // longer declares are dropped (and declared defaults filled in).
    const metadata = reconcileMetadataWithSchema(
      updatedDataSet.metadata,
      updatedDataSet.metadata_schema ?? updatedDataSet.metadataSchema ?? null
    );

    const results = await saveDataSet({ ...updatedDataSet, metadata }, selectedClient.id);

    if (results?.success) {
      const dataSet = results.data_set;
      setSelectedDataSet(dataSet);
      setUpdatedDataSet(dataSet);
      return dataSet.public_id;
    }

    // Return the failure so the page can show the server's message and `errors[]`
    // (e.g. a metadata_schema that isn't valid JSON Schema).
    return results ?? { success: false, message: "Could not save the data set." };
  }


  const shallowDiff = (obj1, obj2) => {
    const diff = {};

    if (obj1 == null) {
      for (const key in obj2) {
        diff[key] = {
          from: null,
          to: obj2[key]
        };
      }
      return diff;
    } else if (obj2 == null) {
      for (const key in obj1) {
        diff[key] = {
          from: obj1[key],
          to: null
        };
      }
      return diff;
    }


    const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

    for (const key of allKeys) {
      if (obj1[key] !== obj2[key]) {
        diff[key] = {
          from: obj1[key],
          to: obj2[key]
        };
      }
    }

    return diff;
  }

  const addDataSetItems = (items) => {
    setSelectedDataSetItems([...selectedDataSetItems, ...items]);
  };

  const removeDataSetItem = (itemId) => {
    setSelectedDataSetItems(selectedDataSetItems.filter((item) => {
      console.log("!!! COMPARING: ", item.public_id, item.id, " TO ", itemId);
      return item.public_id !== itemId && item.id !== itemId
    }));
  };

  const canSaveDataSet = () => {
    if (!updatedDataSet || !updatedDataSet.name) return false;


    // Remove image_data, local_open_date, local_recording_date, and local_start_date from the comparison since they are not part of the actual dataset object and are only used for handling date inputs
    console.log("!!! SHALLOW DIFF: ", shallowDiff(selectedDataSet, updatedDataSet));


    return JSON.stringify(selectedDataSet) !== JSON.stringify(updatedDataSet);
  }

  return (
    <DataSetsContext.Provider value={{ dataSets, setDataSets, selectedDataSet, setSelectedDataSet, addDataSet, removeDataSet, updatedDataSet, setUpdatedDataSet, canSaveDataSet, saveUpdatedDataSet, getDefaultIngestionWorkflow, selectedDataSetItems, setSelectedDataSetItems, addDataSetItems, removeDataSetItem, selectedDataItem, setSelectedDataItem, selectedDataItemChunks, updatedDataItem, setUpdatedDataItem, getDataSetItem, loadingDataSetItemChunks, getDataSetItemChunks, loadingDataSetItem }}>
      {children}
    </DataSetsContext.Provider>
  );
}

export const useDataSets = () => {
  return useContext(DataSetsContext);
}

export default DataSetsContext;
